import { isAxiosError } from 'axios';
import { BASE_URL } from 'consts';
import { CartPrice, CartPriceBookingInput } from 'types/cartPricing';
import { post } from 'utils/axios';

type CartPriceRequest = {
  bookings: CartPriceBookingInput[];
  discountCode?: string;
};
type CartPriceResponse = { data: CartPrice };

export const getCartPriceEndpointUrl = (brandId: string) =>
  `${BASE_URL}/v1/brands/${brandId}/cart-price`;

/**
 * What a cart costs, priced by the server. A POST because the cart is a structured body, but it is a
 * read — and the only correct way for a client to learn a continuous booking's price: the pricing unit
 * is per experience, the rate ladder pro-rates between configured rates, day-based groups bill by whole
 * nights, and taxes are levied per date. All of that is evaluated by the same code that charges.
 */
const fetchCartPrice = async (
  brandId: string,
  bookings: CartPriceBookingInput[],
  options?: { discountCode?: string; signal?: AbortSignal },
): Promise<CartPrice> => {
  const priceWith = async (discountCode?: string) => {
    const response = await post<CartPriceResponse, CartPriceRequest>(
      getCartPriceEndpointUrl(brandId),
      { bookings, ...(discountCode && { discountCode }) },
      { signal: options?.signal },
    );
    return response.data;
  };

  try {
    return await priceWith(options?.discountCode);
  } catch (error) {
    // The endpoint refuses a code it cannot apply to these bookings, with the status the validate route
    // would give. The cart still has a price without it, so the guest is shown that rather than none.
    const status = isAxiosError(error) ? error.response?.status : undefined;
    if (options?.discountCode && status && status >= 400 && status < 500) {
      return priceWith();
    }
    throw error;
  }
};

export default fetchCartPrice;
